import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Empty,
  Flex,
  Input,
  List,
  message,
  Popconfirm,
  Popover,
  Select,
  Segmented,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  AudioMutedOutlined,
  BellOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  WhatsappConversation,
  WhatsappConversationLabel,
  WhatsappConversationMessage,
} from "../../interfaces/WhatsappConversation";
import {
  buscarConversasWhatsapp,
  buscarMensagensConversaWhatsapp,
  excluirMensagemWhatsapp,
  enviarBotoesWhatsapp,
  enviarListaWhatsapp,
  enviarTextoWhatsapp,
  WhatsappSendResponse,
} from "../../services/whatsappConversationService";
import { buscarEtiquetasService } from "../../services/buscarEtiquetasService";
import { setEtiquetasPessoaService } from "../../services/setEtiquetasPessoaService";
import { buscarLeadsByIdService } from "../../services/buscarLeadsByIdService";
import { atualizarLead } from "../../services/atualizarLeadService";
import {
  AtendimentoContexto,
  buscarContextoAtendimento,
  buscarNotasPessoa,
  criarNotaPessoa,
  finalizarEArquivarAtendimento,
  NotaPessoa,
} from "../../services/atendimentoService";

const { Text, Title } = Typography;

const Page = styled.main`
  width: min(1400px, calc(100% - 32px));
  margin: 30px auto 48px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Workspace = styled.section`
  display: grid;
  grid-template-columns: minmax(310px, 380px) minmax(0, 1fr);
  min-height: 620px;
  overflow: visible;

  .ant-card-body {
    height: 100%;
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const ConversationList = styled.div`
  overflow-y: auto;
  border-right: 1px solid #edf0f2;

  @media (max-width: 800px) {
    max-height: 370px;
    border-right: none;
    border-bottom: 1px solid #edf0f2;
  }
`;

const MessagePanel = styled.div<{ $isMobileConversationOpen: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 620px;

  @media (max-width: 800px) {
    display: ${({ $isMobileConversationOpen }) => ($isMobileConversationOpen ? "flex" : "none")};
    position: fixed;
    inset: 0;
    z-index: 1000;
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
    background: #fff;
  }
`;

const MobileBackButton = styled(Button)`
  display: none;

  @media (max-width: 800px) {
    display: inline-flex;
    align-self: flex-start;
    margin: 10px 12px 0;
  }
`;

const MessageHistory = styled.div`
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  padding: 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #f6f8f8;

  @media (max-width: 800px) {
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  }
`;

const Bubble = styled.div<{ $outbound: boolean }>`
  align-self: ${({ $outbound }) => ($outbound ? "flex-end" : "flex-start")};
  max-width: min(80%, 560px);
  padding: 10px 12px;
  border-radius: ${({ $outbound }) => ($outbound ? "12px 12px 2px 12px" : "12px 12px 12px 2px")};
  background: ${({ $outbound }) => ($outbound ? "#d9f0e5" : "#ffffff")};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  white-space: pre-wrap;
  word-break: break-word;
`;

const Composer = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  border-top: 1px solid #edf0f2;
  padding: 16px 20px;
  background: #fff;
`;

const ComposerFields = styled.div`
  display: grid;
  gap: 8px;
`;

const DEFAULT_MESSAGE_SIGNATURE = "Thiago - Atendimento:";
const PREVIOUS_DEFAULT_MESSAGE_SIGNATURES = [
  "William - Atendimento",
  "Thiago - atendimento",
  "Thiago - Atendimento",
];
const MESSAGE_SIGNATURE_STORAGE_KEY = "whatsapp-message-signature";
const WHATSAPP_SOUND_ENABLED_STORAGE_KEY = "whatsapp-sound-enabled";
const NOTIFICATION_COOLDOWN_MS = 5_000;

type Filter = "all" | "pending" | "answered";
type ComposerMode = "text" | "buttons" | "list";

type ButtonDraft = { id: string; title: string };
type ListRowDraft = { id: string; title: string; description: string };
type OptimisticMessage = {
  id: string;
  providerMessageId?: string;
  text: string;
  receivedAt: string;
};
type LoadMessagesOptions = {
  selectConversation?: boolean;
  showLoader?: boolean;
};

type BrowserNotificationPermission = NotificationPermission | "unsupported";

function getSavedSignature() {
  try {
    const savedSignature = localStorage.getItem(MESSAGE_SIGNATURE_STORAGE_KEY);
    return PREVIOUS_DEFAULT_MESSAGE_SIGNATURES.includes(savedSignature ?? "")
      ? DEFAULT_MESSAGE_SIGNATURE
      : savedSignature ?? DEFAULT_MESSAGE_SIGNATURE;
  } catch {
    return DEFAULT_MESSAGE_SIGNATURE;
  }
}

function getSavedSoundEnabled() {
  try {
    return localStorage.getItem(WHATSAPP_SOUND_ENABLED_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

function getBrowserNotificationPermission(): BrowserNotificationPermission {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

function formatPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");

  if (digits.length === 13 && digits.startsWith("55")) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }

  return `+${digits}`;
}

function conversationTitle(conversation: WhatsappConversation) {
  return conversation.contact?.name ?? "Novo contato";
}

function conversationPhone(conversation: WhatsappConversation) {
  return conversation.displayPhone || formatPhone(conversation.phone);
}

function conversationLabels(conversation: WhatsappConversation) {
  return conversation.contact?.labels ?? [];
}

function LabelChips({ labels }: { labels: WhatsappConversationLabel[] }) {
  return (
    <Flex gap={6} wrap="wrap">
      {labels.map((label) => (
        <Tag
          key={label.id}
          style={{
            margin: 0,
            color: "#fff",
            backgroundColor: label.cor || "#1677ff",
            borderColor: label.cor || "#1677ff",
          }}
        >
          {label.nome}
        </Tag>
      ))}
    </Flex>
  );
}

function formatDate(value: string, includeDate = true) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    ...(includeDate ? { day: "2-digit", month: "2-digit" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function preview(conversation: WhatsappConversation) {
  if (conversation.lastText) return conversation.lastText;
  if (conversation.lastActionId) return `Ação selecionada: ${conversation.lastActionId}`;
  return `[${conversation.lastType}]`;
}

function isPendingConversation(conversation: WhatsappConversation) {
  return conversation.atendimentoId !== null && conversation.lastDirection === "inbound";
}

function messageContent(message: WhatsappConversationMessage) {
  if (message.text) return message.text;
  if (message.actionId) return `Ação selecionada: ${message.actionId}`;
  return `[Mensagem do tipo: ${message.type}]`;
}

function noteAuthor(note: NotaPessoa) {
  const criadoPor = [note.criadoPor?.nome, note.criadoPor?.sobrenome].filter(Boolean).join(" ");
  return criadoPor || note.autor?.nome || note.autor?.name || note.autorNome || "Equipe";
}

function renderMessageContent(content: string) {
  const signatureMatch = content.match(/^\*_([\s\S]+?)_\*/);
  if (!signatureMatch) return content;

  return <><strong><em>{signatureMatch[1]}</em></strong>{content.slice(signatureMatch[0].length)}</>;
}

function formatAttempts(response: WhatsappSendResponse) {
  const attempts = response.attempts ?? [];
  if (attempts.length === 0) return "O provedor não confirmou o envio.";

  return attempts
    .map((attempt) => `${attempt.provider}: ${attempt.status}${attempt.statusCode ? ` (${attempt.statusCode})` : ""}`)
    .join("; ");
}

export default function ViewAtendimento() {
  const [conversations, setConversations] = useState<WhatsappConversation[]>([]);
  const [selected, setSelected] = useState<WhatsappConversation | null>(null);
  const [messages, setMessages] = useState<WhatsappConversationMessage[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [isMobileConversationOpen, setIsMobileConversationOpen] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [conversationError, setConversationError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [availableLabels, setAvailableLabels] = useState<WhatsappConversationLabel[]>([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [isSavingLabels, setIsSavingLabels] = useState(false);
  const [isLabelEditorOpen, setIsLabelEditorOpen] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([]);
  const [isLeadNameEditorOpen, setIsLeadNameEditorOpen] = useState(false);
  const [leadNameDraft, setLeadNameDraft] = useState("");
  const [isSavingLeadName, setIsSavingLeadName] = useState(false);
  const [notes, setNotes] = useState<NotaPessoa[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [attendanceContext, setAttendanceContext] = useState<AtendimentoContexto | null>(null);
  const [isLoadingAttendanceContext, setIsLoadingAttendanceContext] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isFinalizingAttendance, setIsFinalizingAttendance] = useState(false);
  const [composerMode, setComposerMode] = useState<ComposerMode>("text");
  const [composerText, setComposerText] = useState("");
  const [signature, setSignature] = useState(getSavedSignature);
  const [isSignatureEnabled, setIsSignatureEnabled] = useState(true);
  const [signatureDraft, setSignatureDraft] = useState(signature);
  const [isSignatureEditorOpen, setIsSignatureEditorOpen] = useState(false);
  const [buttonFooter, setButtonFooter] = useState("");
  const [buttonDrafts, setButtonDrafts] = useState<ButtonDraft[]>([{ id: "", title: "" }]);
  const [listButtonLabel, setListButtonLabel] = useState("Ver opções");
  const [listSectionTitle, setListSectionTitle] = useState("Opções");
  const [listRows, setListRows] = useState<ListRowDraft[]>([{ id: "", title: "", description: "" }]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [optimisticMessage, setOptimisticMessage] = useState<OptimisticMessage | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<number | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(getSavedSoundEnabled);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState<BrowserNotificationPermission>(
    getBrowserNotificationPermission
  );
  const selectedConversationRef = useRef<WhatsappConversation | null>(null);
  const isLoadingConversationsRef = useRef(false);
  const loadingMessagePhoneRef = useRef<string | null>(null);
  const messageHistoryRef = useRef<HTMLDivElement | null>(null);
  const latestMessageRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollToLatestMessageRef = useRef(false);
  const hasLoadedInitialConversationsRef = useRef(false);
  const knownConversationsRef = useRef(new Map<string, { lastMessageAt: string; lastDirection: WhatsappConversation["lastDirection"] }>());
  const lastNotificationAtRef = useRef(new Map<string, number>());
  const audioContextRef = useRef<AudioContext | null>(null);

  const scrollToLatestMessage = useCallback(() => {
    const scrollToEnd = () => {
      const history = messageHistoryRef.current;
      if (!history) return false;

      history.scrollTop = history.scrollHeight;
      latestMessageRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
      return true;
    };

    if (!scrollToEnd()) return;

    window.requestAnimationFrame(() => {
      if (!scrollToEnd()) return;

      window.requestAnimationFrame(() => {
        if (scrollToEnd()) {
          shouldScrollToLatestMessageRef.current = false;
        }
      });
    });
  }, []);

  const setMessageHistoryRef = useCallback((element: HTMLDivElement | null) => {
    messageHistoryRef.current = element;

    if (element && shouldScrollToLatestMessageRef.current) {
      scrollToLatestMessage();
    }
  }, [scrollToLatestMessage]);

  const prepareNotificationSound = useCallback(() => {
    if (!window.AudioContext) return null;

    const context = audioContextRef.current ?? new window.AudioContext();
    audioContextRef.current = context;

    if (context.state === "suspended") {
      void context.resume().catch(() => undefined);
    }

    return context;
  }, []);

  const playNotificationSound = useCallback(() => {
    const context = prepareNotificationSound();
    if (!context) return;

    const play = () => {
      if (context.state !== "running") return;

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 760;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.16);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.17);
    };

    if (context.state === "running") {
      play();
    } else {
      void context.resume().then(play).catch(() => undefined);
    }
  }, [prepareNotificationSound]);

  const notifyNewInboundMessage = useCallback((conversation: WhatsappConversation) => {
    const now = Date.now();
    const lastNotifiedAt = lastNotificationAtRef.current.get(conversation.phone) ?? 0;
    if (now - lastNotifiedAt < NOTIFICATION_COOLDOWN_MS) return;

    lastNotificationAtRef.current.set(conversation.phone, now);

    if (isSoundEnabled) {
      playNotificationSound();
    }

    if (document.visibilityState !== "visible" && getBrowserNotificationPermission() === "granted") {
      const browserNotification = new Notification(`Nova mensagem de ${conversationTitle(conversation)}`, {
        body: preview(conversation).slice(0, 180),
        tag: `whatsapp-conversation-${conversation.phone}`,
      });

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
      };
    }
  }, [isSoundEnabled, playNotificationSound]);

  const loadConversations = useCallback(async (showLoader = true) => {
    if (isLoadingConversationsRef.current) return;

    isLoadingConversationsRef.current = true;
    if (showLoader) {
      setIsLoadingConversations(true);
    }

    try {
      const items = await buscarConversasWhatsapp();
      const ordered = [...items].sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );

      const knownConversations = knownConversationsRef.current;
      if (hasLoadedInitialConversationsRef.current) {
        ordered.forEach((conversation) => {
          const previous = knownConversations.get(conversation.phone);
          const hasNewInboundMessage =
            conversation.lastDirection === "inbound" &&
            (!previous || conversation.lastMessageAt !== previous.lastMessageAt);

          if (hasNewInboundMessage) {
            notifyNewInboundMessage(conversation);
          }
        });
      }

      knownConversationsRef.current = new Map(
        ordered.map((conversation) => [
          conversation.phone,
          { lastMessageAt: conversation.lastMessageAt, lastDirection: conversation.lastDirection },
        ])
      );
      hasLoadedInitialConversationsRef.current = true;
      setConversations(ordered);
      setSelected((current) => {
        const updated = ordered.find((item) => item.phone === current?.phone) ?? current;
        selectedConversationRef.current = updated;
        return updated;
      });
      setConversationError(false);
    } catch (error) {
      console.error(error);
      setConversationError(true);
    } finally {
      isLoadingConversationsRef.current = false;
      if (showLoader) {
        setIsLoadingConversations(false);
      }
    }
  }, [notifyNewInboundMessage]);

  const loadMessages = useCallback(async (
    conversation: WhatsappConversation,
    { selectConversation = true, showLoader = true }: LoadMessagesOptions = {}
  ) => {
    const phone = conversation.phone;

    if (loadingMessagePhoneRef.current === phone) return;

    if (selectConversation) {
      selectedConversationRef.current = conversation;
      setSelected(conversation);
    }

    loadingMessagePhoneRef.current = phone;
    if (showLoader) {
      setIsLoadingMessages(true);
      setMessageError(false);
    }

    try {
      const items = await buscarMensagensConversaWhatsapp(phone);
      console.log("Mensagens recebidas da conversa:", items);

      if (selectedConversationRef.current?.phone !== phone) return;

      setMessages(items);
      setOptimisticMessage((current) =>
        current && items.some((item) => item.providerMessageId === current.providerMessageId) ? null : current
      );
    } catch (error) {
      console.error(error);

      if (selectedConversationRef.current?.phone !== phone || !showLoader) return;

      setMessages([]);
      setMessageError(true);
    } finally {
      if (loadingMessagePhoneRef.current === phone) {
        loadingMessagePhoneRef.current = null;
        if (showLoader) {
          setIsLoadingMessages(false);
        }
      }
    }
  }, []);

  useLayoutEffect(() => {
    if (!shouldScrollToLatestMessageRef.current || isLoadingMessages) return;

    scrollToLatestMessage();
  }, [isLoadingMessages, messages, optimisticMessage, scrollToLatestMessage]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!isSoundEnabled) return;

    const unlockSound = () => {
      prepareNotificationSound();
    };

    document.addEventListener("pointerdown", unlockSound, { once: true });
    return () => document.removeEventListener("pointerdown", unlockSound);
  }, [isSoundEnabled, prepareNotificationSound]);

  useEffect(() => () => {
    const context = audioContextRef.current;
    if (context && context.state !== "closed") {
      void context.close();
    }
  }, []);

  const toggleNotificationSound = useCallback(() => {
    const willEnableSound = !isSoundEnabled;
    setIsSoundEnabled(willEnableSound);

    try {
      localStorage.setItem(WHATSAPP_SOUND_ENABLED_STORAGE_KEY, String(willEnableSound));
    } catch {
      // A preferência continua ativa apenas durante esta sessão.
    }

    if (willEnableSound) {
      prepareNotificationSound();
      message.success("Som de novas mensagens ativado.");
    } else {
      message.info("Som de novas mensagens desativado.");
    }
  }, [isSoundEnabled, prepareNotificationSound]);

  const requestBrowserNotifications = useCallback(async () => {
    if (browserNotificationPermission === "unsupported") {
      message.warning("Este navegador não oferece notificações nativas.");
      return;
    }

    if (browserNotificationPermission === "granted") {
      message.info("As notificações do navegador já estão ativadas.");
      return;
    }

    const permission = await Notification.requestPermission();
    setBrowserNotificationPermission(permission);

    if (permission === "granted") {
      message.success("Notificações do navegador ativadas.");
    } else {
      message.warning("A permissão para notificações não foi concedida.");
    }
  }, [browserNotificationPermission]);

  const refreshInBackground = useCallback(() => {
    void loadConversations(false);

    const openConversation = selectedConversationRef.current;
    if (openConversation) {
      void loadMessages(openConversation, { selectConversation: false, showLoader: false });
    }
  }, [loadConversations, loadMessages]);

  useEffect(() => {
    const intervalId = window.setInterval(refreshInBackground, 30_000);
    document.addEventListener("visibilitychange", refreshInBackground);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshInBackground);
    };
  }, [refreshInBackground]);

  const loadAvailableLabels = useCallback(async () => {
    setIsLoadingLabels(true);

    try {
      const response = await buscarEtiquetasService();
      const labels = Array.isArray(response) ? response : [];
      setAvailableLabels(
        labels
          .filter((label) => label.active === undefined || label.active === 1 || label.active === true)
          .map((label) => ({ id: label.id, nome: label.nome, cor: label.cor ?? null }))
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
      );
    } catch (error) {
      console.error(error);
      message.error("Não foi possível carregar as etiquetas disponíveis.");
    } finally {
      setIsLoadingLabels(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableLabels();
  }, [loadAvailableLabels]);

  const loadNotes = useCallback(async (pessoaId: number) => {
    setIsLoadingNotes(true);
    try {
      setNotes(await buscarNotasPessoa(pessoaId));
    } catch (error) {
      console.error(error);
      message.error("Não foi possível carregar as notas internas.");
    } finally {
      setIsLoadingNotes(false);
    }
  }, []);

  const loadAttendanceContext = useCallback(async (atendimentoId: number) => {
    setIsLoadingAttendanceContext(true);
    try {
      setAttendanceContext(await buscarContextoAtendimento(atendimentoId));
    } catch (error) {
      console.error(error);
      setAttendanceContext(null);
      message.error("Não foi possível carregar o resumo do contexto.");
    } finally {
      setIsLoadingAttendanceContext(false);
    }
  }, []);

  useEffect(() => {
    const pessoaId = selected?.contact?.pessoaId;
    setNoteDraft("");
    setNotes([]);
    setAttendanceContext(null);

    if (pessoaId) {
      void loadNotes(pessoaId);
    }
  }, [loadNotes, selected?.contact?.pessoaId]);

  const saveNote = useCallback(async () => {
    const pessoaId = selected?.contact?.pessoaId;
    const conteudo = noteDraft.trim();
    if (!pessoaId || !conteudo) return;

    setIsSavingNote(true);
    try {
      await criarNotaPessoa(pessoaId, conteudo, selected?.atendimentoId ?? undefined);
      setNoteDraft("");
      await loadNotes(pessoaId);
      message.success("Nota interna adicionada.");
    } catch (error) {
      console.error(error);
      message.error("Não foi possível adicionar a nota interna.");
    } finally {
      setIsSavingNote(false);
    }
  }, [loadNotes, noteDraft, selected]);

  const finalizeAttendance = useCallback(async () => {
    if (!selected?.atendimentoId) return;

    const atendimentoId = selected.atendimentoId;
    setIsFinalizingAttendance(true);
    try {
      await finalizarEArquivarAtendimento(atendimentoId);
      const closedConversation = { ...selected, atendimentoId: null };
      selectedConversationRef.current = closedConversation;
      setSelected(closedConversation);
      setConversations((current) => current.map((conversation) =>
        conversation.phone === selected.phone ? closedConversation : conversation
      ));
      message.success("Atendimento finalizado e arquivado.");
    } catch (error) {
      console.error(error);
      message.error("Não foi possível finalizar o atendimento.");
    } finally {
      setIsFinalizingAttendance(false);
    }
  }, [selected]);

  const saveLeadName = useCallback(async () => {
    const contact = selected?.contact;
    const nome = leadNameDraft.trim();
    if (!selected || !contact || contact.type !== "LEAD" || !nome) return;

    setIsSavingLeadName(true);
    try {
      const lead = await buscarLeadsByIdService(contact.id);
      if (!lead) {
        throw new Error("Lead não encontrado.");
      }

      const updatedLead = await atualizarLead({ ...lead, id: contact.id, nome });
      if (!updatedLead) {
        throw new Error("Não foi possível atualizar o lead.");
      }

      const updatedConversation = {
        ...selected,
        contact: { ...contact, name: nome },
      };
      selectedConversationRef.current = updatedConversation;
      setSelected(updatedConversation);
      setConversations((current) => current.map((conversation) =>
        conversation.phone === selected.phone ? updatedConversation : conversation
      ));
      setIsLeadNameEditorOpen(false);
      message.success("Nome do lead atualizado.");
    } catch (error) {
      console.error(error);
      message.error("Não foi possível atualizar o nome do lead.");
    } finally {
      setIsSavingLeadName(false);
    }
  }, [leadNameDraft, selected]);

  const saveLabels = useCallback(async () => {
    if (!selected?.contact?.pessoaId) return;

    setIsSavingLabels(true);
    try {
      const response = await setEtiquetasPessoaService(selected.contact.pessoaId, selectedLabelIds);
      if (!response) {
        throw new Error("Não foi possível salvar as etiquetas.");
      }

      await loadConversations();
      setIsLabelEditorOpen(false);
      message.success("Etiquetas atualizadas.");
    } catch (error) {
      console.error(error);
      message.error("Não foi possível salvar as etiquetas.");
    } finally {
      setIsSavingLabels(false);
    }
  }, [loadConversations, selected, selectedLabelIds]);

  const saveSignature = useCallback(() => {
    const updatedSignature = signatureDraft.trim();
    setSignature(updatedSignature);
    setIsSignatureEditorOpen(false);

    try {
      localStorage.setItem(MESSAGE_SIGNATURE_STORAGE_KEY, updatedSignature);
    } catch (error) {
      console.error(error);
    }
  }, [signatureDraft]);

  const deleteMessage = useCallback(async (messageToDelete: WhatsappConversationMessage) => {
    if (
      !selected ||
      messageToDelete.provider !== "whapi" ||
      messageToDelete.direction !== "outbound" ||
      messageToDelete.deletedAt !== null ||
      !messageToDelete.providerMessageId
    ) {
      return;
    }

    setDeletingMessageId(messageToDelete.id);
    try {
      await excluirMensagemWhatsapp(messageToDelete.providerMessageId);
      setMessages((current) => current.filter((message) => message.id !== messageToDelete.id));
      message.success("Mensagem excluída.");

      await Promise.all([
        loadMessages(selected),
        loadConversations(false),
      ]);
    } catch (error) {
      console.error(error);
      message.error("Não foi possível excluir a mensagem.");
    } finally {
      setDeletingMessageId(null);
    }
  }, [loadConversations, loadMessages, selected]);

  const copyMessage = useCallback(async (messageId: string | number, content: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = content;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (!copied) throw new Error("Clipboard indisponível");
      }

      const copiedId = String(messageId);
      setCopiedMessageId(copiedId);
      window.setTimeout(() => {
        setCopiedMessageId((currentId) => (currentId === copiedId ? null : currentId));
      }, 1800);
    } catch (error) {
      console.error(error);
      message.error("Não foi possível copiar a mensagem.");
    }
  }, []);

  const sendMessage = useCallback(async () => {
    if (!selected) return;

    const text = composerText.trim();
    if (!text) {
      message.warning("Escreva a mensagem antes de enviar.");
      return;
    }

    const signedText = isSignatureEnabled && signature.trim() ? `*_${signature.trim()}_*\n${text}` : text;
    let response: WhatsappSendResponse;
    let optimisticText = signedText;

    try {
      setIsSendingMessage(true);

      if (composerMode === "text") {
        response = await enviarTextoWhatsapp(selected.phone, signedText);
      } else if (composerMode === "buttons") {
        const buttons = buttonDrafts.map((button) => ({ id: button.id.trim(), title: button.title.trim() }));
        if (buttons.length === 0 || buttons.length > 3 || buttons.some((button) => !button.id || !button.title)) {
          message.warning("Informe de 1 a 3 botões, todos com identificador e título.");
          return;
        }

        response = await enviarBotoesWhatsapp(selected.phone, signedText, buttons, buttonFooter.trim() || undefined);
        optimisticText = `${signedText}\n[Botões: ${buttons.map((button) => button.title).join(", ")}]`;
      } else {
        const rows = listRows.map((row) => ({
          id: row.id.trim(),
          title: row.title.trim(),
          ...(row.description.trim() ? { description: row.description.trim() } : {}),
        }));

        if (
          !listButtonLabel.trim() ||
          !listSectionTitle.trim() ||
          rows.length === 0 ||
          rows.length > 10 ||
          rows.some((row) => !row.id || !row.title)
        ) {
          message.warning("Informe o botão, a seção e de 1 a 10 opções com identificador e título.");
          return;
        }

        response = await enviarListaWhatsapp(selected.phone, signedText, listButtonLabel.trim(), [
          { title: listSectionTitle.trim(), rows },
        ]);
        optimisticText = `${signedText}\n[Lista: ${rows.map((row) => row.title).join(", ")}]`;
      }

      if (!response.accepted) {
        message.error(`Mensagem não confirmada pelo provedor: ${formatAttempts(response)}`);
        return;
      }

      const sentAt = new Date().toISOString();
      setOptimisticMessage({
        id: `optimistic-${Date.now()}`,
        providerMessageId: response.providerMessageId,
        text: optimisticText,
        receivedAt: sentAt,
      });
      setConversations((current) =>
        current.map((conversation) =>
          conversation.phone === selected.phone
            ? {
                ...conversation,
                lastDirection: "outbound",
                lastText: optimisticText,
                lastType: response.renderedAs ?? composerMode,
                lastActionId: null,
                lastMessageAt: sentAt,
                messageCount: conversation.messageCount + 1,
              }
            : conversation
        )
      );
      setComposerText("");

      if (composerMode === "buttons") {
        setButtonFooter("");
        setButtonDrafts([{ id: "", title: "" }]);
      }
      if (composerMode === "list") {
        setListButtonLabel("Ver opções");
        setListSectionTitle("Opções");
        setListRows([{ id: "", title: "", description: "" }]);
      }

      if (response.fallbackUsed) {
        message.warning("O provedor enviou a interação como texto.");
      } else {
        message.success("Mensagem enviada ao provedor.");
      }

      await loadMessages(selected);
    } catch (error) {
      console.error(error);
      message.error("Não foi possível enviar a mensagem.");
    } finally {
      setIsSendingMessage(false);
    }
  }, [
    buttonDrafts,
    buttonFooter,
    composerMode,
    composerText,
    listButtonLabel,
    listRows,
    listSectionTitle,
    loadMessages,
    selected,
    signature,
    isSignatureEnabled,
  ]);

  const visibleConversations = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");
    const normalizedPhoneSearch = search.replace(/\D/g, "");

    return conversations.filter((conversation) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "pending" && isPendingConversation(conversation)) ||
        (filter === "answered" && conversation.lastDirection === "outbound");
      const matchesSearch =
        !normalizedSearch ||
        conversationTitle(conversation).toLocaleLowerCase("pt-BR").includes(normalizedSearch) ||
        conversation.phone.replace(/\D/g, "").includes(normalizedPhoneSearch);

      return matchesFilter && matchesSearch;
    });
  }, [conversations, filter, search]);

  const pendingCount = conversations.filter(isPendingConversation).length;

  return (
    <Page>
      <Flex align="center" justify="space-between" gap={16} wrap="wrap">
        <div>
          <Title level={2} style={{ color: "#003644", margin: 0 }}>
            Atendimento
          </Title>
          <Text type="secondary">Acompanhe as conversas recebidas pelo WhatsApp.</Text>
        </div>
        <Flex align="center" gap={12}>
          <Badge count={pendingCount} overflowCount={99}>
            <Tag color="red" style={{ margin: 0, padding: "5px 10px" }}>
              Aguardando resposta
            </Tag>
          </Badge>
          <Button
            icon={isSoundEnabled ? <SoundOutlined /> : <AudioMutedOutlined />}
            onClick={toggleNotificationSound}
            aria-pressed={isSoundEnabled}
          >
            {isSoundEnabled ? "Som ativado" : "Som desativado"}
          </Button>
          <Button icon={<BellOutlined />} onClick={requestBrowserNotifications}>
            {browserNotificationPermission === "granted"
              ? "Notificações ativadas"
              : browserNotificationPermission === "denied"
                ? "Notificações bloqueadas"
                : "Ativar notificações"}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadConversations()} loading={isLoadingConversations}>
            Atualizar
          </Button>
        </Flex>
      </Flex>

      {conversationError && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar as conversas."
          action={<Button size="small" onClick={() => loadConversations()}>Tentar novamente</Button>}
        />
      )}

      <Card bodyStyle={{ padding: 0, height: "100%" }}>
        <Workspace>
          <ConversationList>
            <div style={{ padding: 16, display: "grid", gap: 12 }}>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Buscar por nome ou telefone"
                allowClear
              />
              <Segmented
                block
                value={filter}
                onChange={(value) => setFilter(value as Filter)}
                options={[
                  { label: "Todas", value: "all" },
                  { label: "Pendentes", value: "pending" },
                  { label: "Respondidas", value: "answered" },
                ]}
              />
            </div>

            {isLoadingConversations ? (
              <div style={{ padding: 16 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
            ) : (
              <List
                dataSource={visibleConversations}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma conversa" /> }}
                renderItem={(conversation) => {
                  const isSelected = selected?.phone === conversation.phone;
                  const pending = isPendingConversation(conversation);

                  return (
                    <List.Item
                      onClick={() => {
                        shouldScrollToLatestMessageRef.current = true;
                        setIsMobileConversationOpen(true);
                        loadMessages(conversation);
                      }}
                      style={{
                        cursor: "pointer",
                        padding: "14px 16px",
                        background: isSelected ? "#eaf4f5" : "transparent",
                        borderLeft: `3px solid ${isSelected ? "#003644" : "transparent"}`,
                      }}
                    >
                      <List.Item.Meta
                        title={
                          <Flex justify="space-between" gap={8}>
                            <Text strong>{conversationTitle(conversation)}</Text>
                            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                              {formatDate(conversation.lastMessageAt)}
                            </Text>
                          </Flex>
                        }
                        description={
                          <Flex vertical gap={5}>
                            <Text type="secondary">{conversationPhone(conversation)}</Text>
                            <Text ellipsis={{ tooltip: preview(conversation) }}>{preview(conversation)}</Text>
                            <Flex gap={6} align="center">
                              {pending ? <Tag color="red">Aguardando resposta</Tag> : <Tag color="green">Respondida</Tag>}
                              <Text type="secondary">{conversation.messageCount} mensagens</Text>
                            </Flex>
                            <LabelChips labels={conversationLabels(conversation)} />
                          </Flex>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </ConversationList>

          <MessagePanel $isMobileConversationOpen={isMobileConversationOpen}>
            {selected ? (
              <>
                <MobileBackButton
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setIsMobileConversationOpen(false)}
                >
                  Voltar
                </MobileBackButton>
                <Flex align="center" justify="space-between" gap={12} wrap="wrap" style={{ padding: "16px 20px", borderBottom: "1px solid #edf0f2" }}>
                  <Flex vertical gap={6}>
                    <Text strong style={{ fontSize: 16 }}>{conversationTitle(selected)}</Text>
                    <Text type="secondary">{conversationPhone(selected)}</Text>
                    <LabelChips labels={conversationLabels(selected)} />
                    <div><Text type="secondary">Canal: {selected.channelId} · Provider: {selected.provider}</Text></div>
                  </Flex>
                  <Flex gap={8} wrap="wrap">
                    {selected.contact?.type === "LEAD" && (
                      <Popover
                        trigger="click"
                        title="Editar nome do lead"
                        open={isLeadNameEditorOpen}
                        onOpenChange={(open) => {
                          setIsLeadNameEditorOpen(open);
                          if (open) {
                            setLeadNameDraft(selected.contact?.name ?? "");
                          }
                        }}
                        content={
                          <Flex vertical gap={12} style={{ width: 280 }}>
                            <Input
                              value={leadNameDraft}
                              onChange={(event) => setLeadNameDraft(event.target.value)}
                              placeholder="Nome do lead"
                              maxLength={120}
                              disabled={isSavingLeadName}
                              onPressEnter={() => void saveLeadName()}
                            />
                            <Flex justify="flex-end" gap={8}>
                              <Button onClick={() => setIsLeadNameEditorOpen(false)}>Cancelar</Button>
                              <Button
                                type="primary"
                                onClick={() => void saveLeadName()}
                                loading={isSavingLeadName}
                                disabled={!leadNameDraft.trim()}
                              >
                                Salvar
                              </Button>
                            </Flex>
                          </Flex>
                        }
                      >
                        <Button icon={<EditOutlined />}>Editar nome</Button>
                      </Popover>
                    )}
                    {selected.contact?.pessoaId && (
                      <Popover
                        trigger="click"
                        title="Editar etiquetas"
                        open={isLabelEditorOpen}
                        onOpenChange={(open) => {
                          setIsLabelEditorOpen(open);
                          if (open) {
                            setSelectedLabelIds(conversationLabels(selected).map((label) => label.id));
                          }
                        }}
                        content={
                          <div style={{ width: 300, display: "grid", gap: 12 }}>
                            <Select
                              mode="multiple"
                              showSearch
                              optionFilterProp="label"
                              placeholder="Selecione as etiquetas"
                              value={selectedLabelIds}
                              onChange={setSelectedLabelIds}
                              loading={isLoadingLabels}
                              options={availableLabels.map((label) => ({ label: label.nome, value: label.id }))}
                            />
                            <Flex justify="flex-end" gap={8}>
                              <Button onClick={() => setSelectedLabelIds([])}>Limpar</Button>
                              <Button type="primary" onClick={saveLabels} loading={isSavingLabels}>Salvar</Button>
                            </Flex>
                          </div>
                        }
                      >
                        <Button>Etiquetas</Button>
                      </Popover>
                    )}
                    <Button icon={<ReloadOutlined />} onClick={() => loadMessages(selected)} loading={isLoadingMessages}>
                      Atualizar
                    </Button>
                  </Flex>
                </Flex>

                <Drawer
                  title={`Notas internas — ${conversationTitle(selected)}`}
                  placement="right"
                  width="min(420px, 100vw)"
                  open={isNotesOpen}
                  onClose={() => setIsNotesOpen(false)}
                  footer={
                    <Flex vertical gap={8}>
                      <Input.TextArea
                        value={noteDraft}
                        onChange={(event) => setNoteDraft(event.target.value)}
                        placeholder="Adicionar nota interna"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        maxLength={2_000}
                        disabled={isSavingNote}
                      />
                      <Flex justify="flex-end">
                        <Button
                          type="primary"
                          onClick={saveNote}
                          loading={isSavingNote}
                          disabled={!noteDraft.trim()}
                        >
                          Adicionar nota
                        </Button>
                      </Flex>
                    </Flex>
                  }
                >
                  <Flex vertical gap={18}>
                    {selected.atendimentoId && (
                      <div style={{ padding: 14, border: "1px solid #d9e8eb", borderRadius: 8, background: "#f6fafa" }}>
                        <Text strong>Resumo do contexto</Text>
                        {isLoadingAttendanceContext ? (
                          <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: 10 }} />
                        ) : attendanceContext ? (
                          <Flex vertical gap={8} style={{ marginTop: 10 }}>
                            <Text type="secondary">
                              Origem: {attendanceContext.customer.identidade?.origem ?? "Não informada"} · Modo: {attendanceContext.atendimento.modo ?? "Não informado"}
                            </Text>
                            <Flex gap={6} wrap="wrap">
                              <Tag color={attendanceContext.customer.resumo.temUsuario ? "blue" : "default"}>
                                Usuário: {attendanceContext.customer.resumo.temUsuario ? "Sim" : "Não"}
                              </Tag>
                              <Tag color={attendanceContext.customer.resumo.temLead ? "blue" : "default"}>
                                Lead: {attendanceContext.customer.resumo.temLead ? "Sim" : "Não"}
                              </Tag>
                              <Tag>Propriedades: {attendanceContext.customer.resumo.quantidadePropriedades}</Tag>
                              <Tag color={attendanceContext.customer.resumo.temAnuncioAtivo ? "green" : "default"}>
                                Anúncio ativo: {attendanceContext.customer.resumo.temAnuncioAtivo ? "Sim" : "Não"}
                              </Tag>
                              <Tag color={attendanceContext.customer.resumo.temPeriodoAtivo ? "green" : "default"}>
                                Período ativo: {attendanceContext.customer.resumo.temPeriodoAtivo ? "Sim" : "Não"}
                              </Tag>
                              <Tag color={attendanceContext.customer.resumo.temAssinaturaAtiva ? "green" : "default"}>
                                Assinatura ativa: {attendanceContext.customer.resumo.temAssinaturaAtiva ? "Sim" : "Não"}
                              </Tag>
                              <Tag color={attendanceContext.customer.resumo.temSolicitacaoReservaAberta ? "orange" : "default"}>
                                Reserva aberta: {attendanceContext.customer.resumo.temSolicitacaoReservaAberta ? "Sim" : "Não"}
                              </Tag>
                            </Flex>
                          </Flex>
                        ) : (
                          <Text type="secondary" style={{ display: "block", marginTop: 10 }}>
                            Resumo indisponível para este atendimento.
                          </Text>
                        )}
                      </div>
                    )}
                    <Text strong>Notas internas</Text>
                    {isLoadingNotes ? (
                      <Skeleton active paragraph={{ rows: 7 }} />
                    ) : notes.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma nota interna cadastrada." />
                    ) : (
                      <Flex vertical gap={14}>
                      {notes.map((note) => (
                        <div key={note.id} style={{ paddingBottom: 14, borderBottom: "1px solid #f0f0f0" }}>
                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginBottom: 5 }}>{note.conteudo}</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {noteAuthor(note)} · {formatDate(note.createdAt)}
                          </Text>
                        </div>
                      ))}
                      </Flex>
                    )}
                  </Flex>
                </Drawer>

                {messageError ? (
                  <div style={{ padding: 24 }}>
                    <Alert
                      type="error"
                      showIcon
                      message="Não foi possível carregar o histórico desta conversa."
                      action={<Button size="small" onClick={() => loadMessages(selected)}>Tentar novamente</Button>}
                    />
                  </div>
                ) : isLoadingMessages ? (
                  <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 10 }} /></div>
                ) : messages.length === 0 && !optimisticMessage ? (
                  <Flex flex={1} align="center" justify="center"><Empty description="Nenhuma mensagem encontrada" /></Flex>
                ) : (
                  <MessageHistory ref={setMessageHistoryRef}>
                    {messages.map((message) => (
                      <Bubble key={message.id} $outbound={message.direction === "outbound"}>
                        <div>{renderMessageContent(messageContent(message))}</div>
                        <Flex justify="space-between" gap={12} align="center" style={{ marginTop: 5 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {message.direction === "outbound" ? "Empresa" : "Cliente"}
                          </Text>
                          <Flex gap={4} align="center">
                            <Button
                              type="text"
                              size="small"
                              icon={copiedMessageId === String(message.id) ? <CheckOutlined /> : <CopyOutlined />}
                              onClick={() => copyMessage(message.id, messageContent(message))}
                              aria-label={copiedMessageId === String(message.id) ? "Mensagem copiada" : "Copiar mensagem"}
                              title={copiedMessageId === String(message.id) ? "Mensagem copiada" : "Copiar mensagem"}
                            />
                            {message.provider === "whapi" &&
                              message.direction === "outbound" &&
                              message.deletedAt === null && (
                              <Popconfirm
                                title="Excluir mensagem?"
                                description="Esta ação não pode ser desfeita."
                                okText="Excluir"
                                cancelText="Cancelar"
                                okButtonProps={{ danger: true, loading: deletingMessageId === message.id }}
                                onConfirm={() => deleteMessage(message)}
                              >
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  loading={deletingMessageId === message.id}
                                  aria-label="Excluir mensagem"
                                />
                              </Popconfirm>
                            )}
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <ClockCircleOutlined /> {formatDate(message.receivedAt)}
                            </Text>
                          </Flex>
                        </Flex>
                      </Bubble>
                    ))}
                    {optimisticMessage && (
                      <Bubble key={optimisticMessage.id} $outbound>
                        <div>{renderMessageContent(optimisticMessage.text)}</div>
                        <Flex justify="space-between" gap={12} align="center" style={{ marginTop: 5 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>Empresa</Text>
                          <Flex gap={4} align="center">
                            <Button
                              type="text"
                              size="small"
                              icon={copiedMessageId === optimisticMessage.id ? <CheckOutlined /> : <CopyOutlined />}
                              onClick={() => copyMessage(optimisticMessage.id, optimisticMessage.text)}
                              aria-label={copiedMessageId === optimisticMessage.id ? "Mensagem copiada" : "Copiar mensagem"}
                              title={copiedMessageId === optimisticMessage.id ? "Mensagem copiada" : "Copiar mensagem"}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <ClockCircleOutlined /> Enviando
                            </Text>
                          </Flex>
                        </Flex>
                      </Bubble>
                    )}
                    <div ref={latestMessageRef} aria-hidden="true" />
                  </MessageHistory>
                )}
                <Composer>
                  <Flex vertical gap={10}>
                    <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                      <Segmented
                        value={composerMode}
                        onChange={(value) => setComposerMode(value as ComposerMode)}
                        options={[
                          { label: "Texto", value: "text" },
                          { label: "Botões", value: "buttons" },
                          { label: "Lista", value: "list" },
                        ]}
                      />
                      <Flex gap={8} wrap="wrap">
                        <Flex gap={0}>
                          <Button
                            type={isSignatureEnabled ? "primary" : "default"}
                            onClick={() => setIsSignatureEnabled((enabled) => !enabled)}
                          >
                            Assinatura
                          </Button>
                          <Popover
                            trigger="click"
                            title="Assinatura da mensagem"
                            open={isSignatureEditorOpen}
                            onOpenChange={(open) => {
                              setIsSignatureEditorOpen(open);
                              if (open) {
                                setSignatureDraft(signature);
                              }
                            }}
                            content={
                              <Flex vertical gap={12} style={{ width: 280 }}>
                                <Text type="secondary">Ela será inserida no início, em negrito e itálico.</Text>
                                <Input
                                  value={signatureDraft}
                                  onChange={(event) => setSignatureDraft(event.target.value)}
                                  placeholder="Ex.: Thiago - Atendimento:"
                                  maxLength={100}
                                />
                                <Flex justify="flex-end" gap={8}>
                                  <Button onClick={() => setIsSignatureEditorOpen(false)}>Cancelar</Button>
                                  <Button type="primary" onClick={saveSignature}>Salvar</Button>
                                </Flex>
                              </Flex>
                            }
                          >
                            <Button icon={<EditOutlined />} aria-label="Editar assinatura" />
                          </Popover>
                        </Flex>
                        {selected.contact?.pessoaId && (
                          <Button
                            onClick={() => {
                              setIsNotesOpen(true);
                              void loadNotes(selected.contact!.pessoaId);
                              if (selected.atendimentoId) {
                                void loadAttendanceContext(selected.atendimentoId);
                              }
                            }}
                          >
                            Notas internas
                          </Button>
                        )}
                        {selected.atendimentoId && (
                          <Popconfirm
                            title="Finalizar e arquivar atendimento?"
                            description="A pessoa ficará disponível para um novo atendimento. As etiquetas serão mantidas."
                            okText="Finalizar"
                            cancelText="Cancelar"
                            okButtonProps={{ danger: true, loading: isFinalizingAttendance }}
                            onConfirm={finalizeAttendance}
                          >
                            <Button danger loading={isFinalizingAttendance}>Finalizar atendimento</Button>
                          </Popconfirm>
                        )}
                      </Flex>
                    </Flex>
                    <Input.TextArea
                      value={composerText}
                      onChange={(event) => setComposerText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" || event.nativeEvent.isComposing) return;

                        event.preventDefault();

                        if (event.ctrlKey) {
                          const input = event.currentTarget;
                          const { selectionStart, selectionEnd } = input;

                          setComposerText((current) =>
                            `${current.slice(0, selectionStart)}\n${current.slice(selectionEnd)}`
                          );
                          window.requestAnimationFrame(() => {
                            input.selectionStart = selectionStart + 1;
                            input.selectionEnd = selectionStart + 1;
                          });
                          return;
                        }

                        void sendMessage();
                      }}
                      placeholder={composerMode === "text" ? "Escreva uma mensagem" : "Texto da mensagem"}
                      autoSize={{ minRows: 2, maxRows: 5 }}
                      disabled={isSendingMessage}
                    />

                    {composerMode === "buttons" && (
                      <ComposerFields>
                        <Input
                          value={buttonFooter}
                          onChange={(event) => setButtonFooter(event.target.value)}
                          placeholder="Rodapé (opcional)"
                          disabled={isSendingMessage}
                        />
                        {buttonDrafts.map((button, index) => (
                          <Flex key={index} gap={8}>
                            <Input
                              value={button.id}
                              onChange={(event) => setButtonDrafts((current) => current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, id: event.target.value } : item
                              ))}
                              placeholder="Identificador"
                              disabled={isSendingMessage}
                            />
                            <Input
                              value={button.title}
                              onChange={(event) => setButtonDrafts((current) => current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, title: event.target.value } : item
                              ))}
                              placeholder="Título do botão"
                              disabled={isSendingMessage}
                            />
                            {buttonDrafts.length > 1 && (
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => setButtonDrafts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                                disabled={isSendingMessage}
                                aria-label="Remover botão"
                              />
                            )}
                          </Flex>
                        ))}
                        {buttonDrafts.length < 3 && (
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => setButtonDrafts((current) => [...current, { id: "", title: "" }])}
                            disabled={isSendingMessage}
                          >
                            Adicionar botão
                          </Button>
                        )}
                      </ComposerFields>
                    )}

                    {composerMode === "list" && (
                      <ComposerFields>
                        <Flex gap={8}>
                          <Input
                            value={listButtonLabel}
                            onChange={(event) => setListButtonLabel(event.target.value)}
                            placeholder="Texto do botão da lista"
                            disabled={isSendingMessage}
                          />
                          <Input
                            value={listSectionTitle}
                            onChange={(event) => setListSectionTitle(event.target.value)}
                            placeholder="Título da seção"
                            disabled={isSendingMessage}
                          />
                        </Flex>
                        {listRows.map((row, index) => (
                          <Flex key={index} gap={8} vertical>
                            <Flex gap={8}>
                              <Input
                                value={row.id}
                                onChange={(event) => setListRows((current) => current.map((item, itemIndex) =>
                                  itemIndex === index ? { ...item, id: event.target.value } : item
                                ))}
                                placeholder="Identificador"
                                disabled={isSendingMessage}
                              />
                              <Input
                                value={row.title}
                                onChange={(event) => setListRows((current) => current.map((item, itemIndex) =>
                                  itemIndex === index ? { ...item, title: event.target.value } : item
                                ))}
                                placeholder="Título da opção"
                                disabled={isSendingMessage}
                              />
                              {listRows.length > 1 && (
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => setListRows((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                                  disabled={isSendingMessage}
                                  aria-label="Remover opção"
                                />
                              )}
                            </Flex>
                            <Input
                              value={row.description}
                              onChange={(event) => setListRows((current) => current.map((item, itemIndex) =>
                                itemIndex === index ? { ...item, description: event.target.value } : item
                              ))}
                              placeholder="Descrição (opcional)"
                              disabled={isSendingMessage}
                            />
                          </Flex>
                        ))}
                        {listRows.length < 10 && (
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => setListRows((current) => [...current, { id: "", title: "", description: "" }])}
                            disabled={isSendingMessage}
                          >
                            Adicionar opção
                          </Button>
                        )}
                      </ComposerFields>
                    )}

                    <Flex justify="flex-end">
                      <Button type="primary" icon={<SendOutlined />} onClick={sendMessage} loading={isSendingMessage}>
                        Enviar
                      </Button>
                    </Flex>
                  </Flex>
                </Composer>
              </>
            ) : (
              <Flex vertical flex={1} align="center" justify="center" gap={12} style={{ color: "#65717a" }}>
                <MessageOutlined style={{ fontSize: 38 }} />
                <Text type="secondary">Selecione uma conversa para visualizar o histórico.</Text>
              </Flex>
            )}
          </MessagePanel>
        </Workspace>
      </Card>
    </Page>
  );
}
